import { useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import {
  closeAccessControlModal,
  useAccessControlTemplateSelector,
  useTemplateMetaDataSelector,
} from 'screens/admin/Objectives/GoalPreferences/slice/accessControlSlice';

import { AccessControl, AppNavigationOutlined, Calculator, CascadedViewV2 } from 'assets/assetsV1/iconComponents';
import { DisplayName as DisplayNameIcon } from 'assets/assetsV1/iconComponents/DisplayName';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { sendCreatedNewGoalsTemplateEvent } from 'constants/mixpanelHelper';

import { BottomDrawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader } from 'uiToolkitV1/BottomDrawer';
import { ListItemLeftNav } from 'uiToolkitV1/ListItemLeftNav';

import { REACT_QUERY_GOALS_ACCESS_CONTROL } from '../constants';
import { createTemplateV1, updateTemplateV1 } from '../services';
import AccessControlMatrix from './AccessControlMatrix';
import CalculateProgress from './components/CalculateProgress';
import Cascade from './components/Cascade';
import DisplayName from './components/DisplayName';
import Footer from './components/Footer';
import Header from './components/Header';
import VisibilitySettings from './components/VisibilitySettings';
import { SectionCard } from './components/common/SectionCard';

const AccessControlModal = ({ HeaderComp = Header, FooterComp = Footer }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const toast = useToast();

  const displayContainerRef = useRef<HTMLDivElement>();
  const cascadeContainerRef = useRef<HTMLDivElement>();
  const visibilityContainer = useRef<HTMLDivElement>();
  const calculateContainerRef = useRef<HTMLDivElement>();
  const accessControlRef = useRef<HTMLDivElement>();

  const [scrollIndex, setScrollIndex] = useState(0);

  const scrollComponentIntoView = (ref: React.RefObject<HTMLElement>) => {
    if (ref?.current) {
      setTimeout(() => {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  };

  const navigationArray = [
    {
      title: 'Display name',
      onClick: () => {
        scrollComponentIntoView(displayContainerRef);
        setScrollIndex(0);
      },
      Icon: DisplayNameIcon,
    },
    {
      title: 'How to cascade',
      onClick: () => {
        scrollComponentIntoView(cascadeContainerRef);
        setScrollIndex(1);
      },
      Icon: CascadedViewV2,
    },
    {
      title: 'Visibility settings',
      onClick: () => {
        scrollComponentIntoView(visibilityContainer);
        setScrollIndex(2);
      },
      Icon: AppNavigationOutlined,
    },
    {
      title: 'How to calculate progress',
      onClick: () => {
        scrollComponentIntoView(calculateContainerRef);
        setScrollIndex(3);
      },
      Icon: Calculator,
    },
    {
      title: 'Access control',
      onClick: () => {
        scrollComponentIntoView(accessControlRef);
        setScrollIndex(4);
      },
      Icon: AccessControl,
    },
  ];

  const { isOpen, isEditMode } = useTemplateMetaDataSelector();

  const closeModal = () => {
    dispatch(closeAccessControlModal());
  };

  const queryClient = useQueryClient();

  const { accessControlTemplate } = useAccessControlTemplateSelector();

  const onSave = async () => {
    if (accessControlTemplate?.id) {
      try {
        await updateTemplateV1(accessControlTemplate?.id, accessControlTemplate);
        // To refresh the application with new template config by calling authenticate api on reload
        window.location.reload();
        queryClient.invalidateQueries([REACT_QUERY_GOALS_ACCESS_CONTROL.allTemplates]);
        toast({
          title: `Template has been updated successfully`,
          status: TOAST_STATUSES.SUCCESS,
          position: 'bottom-left',
        });
      } catch (error) {
        console.error('Error updating the template ', error);
        toast({
          title: `Error updating the template. Please try again`,
          status: TOAST_STATUSES.ERROR,
          position: 'bottom-left',
        });
      }
    } else {
      try {
        await createTemplateV1(accessControlTemplate);
        queryClient.invalidateQueries([REACT_QUERY_GOALS_ACCESS_CONTROL.allTemplates]);
        toast({
          title: `Template has been created successfully`,
          status: TOAST_STATUSES.SUCCESS,
          position: 'bottom-left',
        });
        sendCreatedNewGoalsTemplateEvent();
      } catch (error) {
        console.error('Error creating the template ', error);
        toast({
          title: `Error creating the template. Please try again`,
          status: TOAST_STATUSES.ERROR,
          position: 'bottom-left',
        });
      }
    }
    closeModal();
  };

  const showFooter = !accessControlTemplate?.id || isEditMode;

  return (
    <BottomDrawer isOpen={isOpen} placement="bottom" onClose={closeModal}>
      <DrawerContent width="80%" marginLeft="auto !important" marginRight="auto !important">
        {/*  @ts-ignore */}

        <DrawerHeader onClose={closeModal}>
          <HeaderComp onClose={closeModal} />
        </DrawerHeader>
        {/*  @ts-ignore */}
        <DrawerBody mt="4px" display="flex" overflow="hidden">
          <StyledFlex>
            <Flex
              backgroundColor="white"
              py={2}
              width="16%"
              //  @ts-ignore
              borderRight={`5px solid ${theme.colors.neutral.EBEFF3}`}
            >
              <Box w="100%" p="8px">
                {navigationArray.map((navItem, index) => (
                  // @ts-ignore
                  <ListItemLeftNav
                    key={index}
                    active={index === scrollIndex}
                    LeftIconComponent={navItem.Icon}
                    title={navItem.title}
                    onClick={navItem.onClick}
                  />
                ))}
              </Box>
            </Flex>
            <Flex flexDir="column" flex="1" overflow="auto" bg="#EBEFF3">
              <Flex direction="column" rowGap={1}>
                <SectionCard ref={displayContainerRef}>
                  <DisplayName isEditMode={isEditMode} />
                </SectionCard>

                <SectionCard ref={displayContainerRef}>
                  <Cascade isEditMode={isEditMode} />
                </SectionCard>

                <SectionCard ref={visibilityContainer}>
                  <VisibilitySettings isEditMode={isEditMode} />
                </SectionCard>

                <SectionCard ref={calculateContainerRef}>
                  <CalculateProgress isEditMode={isEditMode} />
                </SectionCard>

                <SectionCard ref={accessControlRef}>
                  <AccessControlMatrix />
                </SectionCard>
              </Flex>
            </Flex>
          </StyledFlex>
        </DrawerBody>
        {showFooter && (
          <DrawerFooter>
            <FooterComp onClose={closeModal} onSubmit={onSave} disableSubmit={false} isLoading={false} />
          </DrawerFooter>
        )}
      </DrawerContent>
    </BottomDrawer>
  );
};

const StyledFlex = styled(Flex)`
  height: calc(100% - 8px);
  border: 4px solid #ebeff3;
  margin: 0 8px 8px 8px;
  border-radius: 8px;
  width: 100%;
`;

export { AccessControlModal };
